import { useEffect, useRef, useState } from 'react';
import { ReactComponent as EditIcon } from '$icons/EditIcon.svg';
import { ReactComponent as Check } from '$icons/CheckIconGreen.svg';
import { ReactComponent as Close } from '$icons/CloseX.svg';
import { ReactComponent as Arrow } from '$icons/Chevron.svg';
import useOnClickOutside from '$hooks/useOnClickOutside';

const CandidateModifyStatus = ({
  title,
  onSave,
  id,
  value,
  options,
}: {
  title: string;
  onSave: (value: string) => Promise<void>;
  id: string;
  value: string | undefined | null;
  options: { value: string; text: string; style: string }[];
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);
  const optionsRef = useRef<HTMLDivElement>(null);

  const onSaveHandler = async () => {
    if (value !== selectedValue && selectedValue) await onSave(selectedValue);
    setIsEditMode(false);
  };

  useOnClickOutside(optionsRef, () => {
    setIsOpen(false);
  });

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  return (
    <div className="w-full">
      <div className="flex flex-row justify-between mb-2">
        <label
          htmlFor={id}
          className="text-left w-full font-semibold text-slate-500"
        >
          {title}
        </label>
        {!isEditMode && (
          <button
            aria-label="edit"
            type="button"
            onClick={() => setIsEditMode(true)}
          >
            <EditIcon className="fill-slate-500" />
          </button>
        )}
        {isEditMode && (
          <div className="flex flex-row gap-2">
            <button aria-label="edit" type="button" onClick={onSaveHandler}>
              <Check />
            </button>
            <button
              aria-label="edit"
              type="button"
              onClick={() => setIsEditMode(false)}
            >
              <Close className="fill-red-600 w-4" />
            </button>
          </div>
        )}
      </div>

      {!isEditMode && (
        <div
          className={`mb-4 w-full py-4 px-2 rounded font-semibold text-white ${
            options?.find((singleOpt) => singleOpt.value === selectedValue)
              ?.style || 'bg-gray-400'
          }`}
        >
          {selectedValue || 'N/A'}
        </div>
      )}

      {isEditMode && (
        <div className="relative" ref={optionsRef}>
          <button
            type="button"
            className={`mb-4 w-full py-4 px-2 rounded  font-semibold text-white flex justify-between items-center ${
              options?.find((singleOpt) => singleOpt.value === selectedValue)
                ?.style || 'bg-gray-400'
            }`}
            onClick={() => setIsOpen((prev) => !prev)}
          >
            <span>{selectedValue || 'N/A'}</span>
            <span>
              <Arrow className="fill-white" />
            </span>
          </button>
          {isOpen && (
            <div className="absolute -mt-2 bg-white text-slate-800 w-full border border-slate-500 rounded overflow-hidden z-10">
              {options.map((singleOpt) => {
                return (
                  <button
                    className="px-2 py-1 cursor-pointer w-full text-left hover:bg-gray-200"
                    key={`${singleOpt.value}`}
                    onClick={() => {
                      setSelectedValue(singleOpt.value);
                      setIsOpen(false);
                    }}
                    type="button"
                  >
                    {singleOpt.text}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CandidateModifyStatus;
